import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Tooltip,
  Button,
  Dialog,
  CircularProgress,
  Autocomplete,
  IconButton,
  TextField,
  Box,
  Skeleton,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import axios from 'axios'
import { toast } from 'react-toastify'
import { useAuthContext } from '../../hooks/useAuthContext'
import { formatUserFullName } from '../../utility/userName'
import ApproveRejectSwitch from './ApproveRejectSwitch'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import SnoozeIcon from '@mui/icons-material/Snooze'

import {
  ACTIVITY_TYPES,
  TOAST_SETTINGS,
  TOAST_SETTINGS_ERROR,
  FIELD_TYPES,
  PROCESS_ACTIVITY_STATUSES,
  ROUTE_TYPES,
  FORMATS,
  VOTE_OPTIONS,
} from '../../utility/constants'
import { processDataService } from '../../services/processDataService'
import { formatCurrency } from '../../pages/DataEntryPage/utils/currency'
import LongMenu from '../../pages/DataEntryPage/Fields/LongMenu'
import { GetApp, Visibility } from '@mui/icons-material'
import FileViewer from './PreviewComponent/components/FileViewer'
import MultilineEllipsisWithTooltip from './MultilineEllipsisWithTooltip'
import ConfirmationBox from './ConfirmationBox'
import { getFieldForSetting } from '../../pages/DataEntryPage/utils/fieldUtil'
import TableSetting from '../../../src/components/Dashboard/PreviewComponent/TableSettings/TableSetting'
import { useMyTasks } from '../../context/MyTasksContext'
import { userTrackingStorage } from '../../../src/utility/storageUtils'

const NODE_BASE_URL = process.env.REACT_APP_NODE_BASE_URL

const MyTaskTableView = ({ tasks, activityType, setActivityType, users, onNavigate }) => {
  const { user } = useAuthContext()
  const navigate = useNavigate()
  const { saveScrollPosition } = useMyTasks()

  // ── interaction state ──────────────────────────────────────────────────────
  const [overallComments, setOverallComments] = useState({})
  const [fieldComments, setFieldComments] = useState({})
  const [voteValues, setVoteValues] = useState({})
  const [selectedRoutes, setSelectedRoutes] = useState({})
  const [isSaving, setIsSaving] = useState(false)
  const [expanded, setExpanded] = useState(0)
  const [expandingAccordionId, setExpandingAccordionId] = useState(null)
  const [isActivityTypeLoading, setIsActivityTypeLoading] = useState(false)
  const [isFileViewerOpen, setIsFileViewerOpen] = useState(false)
  const [selectedFileKey, setSelectedFileKey] = useState(null)
  const [fileLoading, setFileLoading] = useState(new Set())
  const [isConfirmationBoxOpen, setIsConfirmationBoxOpen] = useState(false)
  const [confirmationTitle, setConfirmationTitle] = useState('')
  const [pendingTemplateId, setPendingTemplateId] = useState(null)
  const [skeletonAccordionId, setSkeletonAccordionId] = useState(null)

  const [hiddenColumnsByTemplate, setHiddenColumnsByTemplate] = useState(
    () => userTrackingStorage.getForUser(user?.id)?.hiddenColumnsByTemplate || {},
  )

  // Skip the first render so we don't persist the default activityType
  // value (REVIEW) before MyTasks.jsx has hydrated it from storage.
  const persistReadyRef = useRef(false)
  useEffect(() => {
    if (!persistReadyRef.current) {
      persistReadyRef.current = true
      return
    }
    if (!user?.id) return
    userTrackingStorage.setForUser(user.id, { activityType, hiddenColumnsByTemplate })
  }, [user?.id, activityType, hiddenColumnsByTemplate])

  // Show loader when activityType changes
  useEffect(() => {
    setIsActivityTypeLoading(true)
    const timer = setTimeout(() => setIsActivityTypeLoading(false), 300)
    return () => clearTimeout(timer)
  }, [activityType])

  // Derived data
  const { filteredTasks, groupedTasks } = useMemo(() => {
    if (!tasks || !tasks.length) return { filteredTasks: [], groupedTasks: {} }

    const filtered = tasks.filter(
      (task) =>
        task.activity_type === activityType ||
        (task.activity_type === ACTIVITY_TYPES.LONG_RUNNING &&
          activityType === ACTIVITY_TYPES.DATA_ENTRY),
    )

    const grouped = filtered.reduce((acc, task) => {
      const {
        process_template_id,
        process_template_name,
        processDashboardFields,
        processDashboardValues,
        process_route_templates,
        process_id,
        activity_template_id,
        process_activity_status,
        overall_comments,
        selected_route_template_id,
        process_reference_num,
        activity_template_name,
        process_version,
        non_default_currency,
        is_immediate_action_required,
        activity_type,
        process_reference,
        process_data_created_at,
      } = task

      if (!acc[process_template_id]) {
        acc[process_template_id] = {
          name: process_template_name,
          fields: processDashboardFields,
          tasks: [],
        }
      }

      const visibleFields = processDashboardFields?.filter((field) => !field.is_hidden)
      const visibleValues = processDashboardValues?.filter(
        (_, idx) => !processDashboardFields[idx].is_hidden,
      )

      const isDuplicate = acc[process_template_id].tasks.find((t) => t.processId === process_id)
      if (!isDuplicate) {
        acc[process_template_id].tasks.push({
          reference: process_reference,
          values: visibleValues,
          voteOptions: process_route_templates.map((route) => ({
            name: route.route_template_name,
            route_type: route.route_type,
            id: route.id,
          })),
          routeTemplates: process_route_templates,
          processId: process_id,
          activityTemplateId: activity_template_id,
          status: process_activity_status,
          overallComment: overall_comments,
          selectedRouteTemplateId: selected_route_template_id,
          processReference: process_reference_num,
          process_version,
          activity_template_name,
          ndc: non_default_currency,
          is_immediate_action_required,
          activity_type,
          process_data_created_at,
        })
      }

      acc[process_template_id].fields = visibleFields
      return acc
    }, {})

    Object.values(grouped).forEach((group) => {
      group.tasks.sort(
        (a, b) =>
          new Date(b.process_data_created_at || 0) - new Date(a.process_data_created_at || 0),
      )
    })

    const sortedGrouped = Object.fromEntries(
      Object.entries(grouped).sort(([, gA], [, gB]) => {
        const aDate = new Date(gA.tasks?.[0]?.process_data_created_at || 0)
        const bDate = new Date(gB.tasks?.[0]?.process_data_created_at || 0)
        return bDate - aDate
      }),
    )

    return { filteredTasks: filtered, groupedTasks: sortedGrouped }
  }, [tasks, activityType])

  const noTasksForCurrentType = !isSaving && filteredTasks.length === 0

  // Helpers
  const getOverallComment = (route) => {
    if (route?.route_type === 'progress') return 'Ok'
    if (route?.route_type === 'neutral') return 'Please check'
    if (route?.route_type === 'regress') return 'Please revisit'
    return ''
  }

  const getFieldComment = (value) => {
    if (value === VOTE_OPTIONS.APPROVE) return 'Approved'
    if (value === VOTE_OPTIONS.NA) return ''
    if (value === VOTE_OPTIONS.REJECT) return 'Rejected'
    return ''
  }

  // Event Handlers
  const handleSwitchChange = useCallback((processId, value, options) => {
    const voteOptions = options.filter((route) => {
      if (value === VOTE_OPTIONS.APPROVE)
        return route.route_type === ROUTE_TYPES.PROGRESS || route.route_type === ROUTE_TYPES.NEUTRAL
      if (value === VOTE_OPTIONS.REJECT)
        return route.route_type === ROUTE_TYPES.REGRESS || route.route_type === ROUTE_TYPES.NEUTRAL
      return false
    })

    setVoteValues((prev) => ({ ...prev, [processId]: value }))
    setSelectedRoutes((prev) => ({ ...prev, [processId]: voteOptions[0]?.id }))
    setOverallComments((prev) => ({ ...prev, [processId]: getOverallComment(voteOptions[0]) }))
    setFieldComments((prev) => ({ ...prev, [processId]: getFieldComment(value) }))
  }, [])

  const tasksById = useMemo(() => {
    return Object.fromEntries(filteredTasks.map((t) => [t.process_id, t]))
  }, [filteredTasks])

  const handleVoteChange = useCallback(
    (processId, routeId) => {
      const task = tasksById[processId]
      const selectedRoute = task?.process_route_templates?.find((tmpl) => tmpl.id === routeId)
      if (selectedRoute) {
        setSelectedRoutes((prev) => ({ ...prev, [processId]: selectedRoute.id }))
        setOverallComments((prev) => ({ ...prev, [processId]: getOverallComment(selectedRoute) }))
      }
    },
    [tasksById],
  )

  const handleGoClick = useCallback(
    async (templateId) => {
      const group = groupedTasks[templateId]
      if (!group) return

      const selectedTasks = group.tasks
        .filter((task) => (voteValues[task.processId] || VOTE_OPTIONS.NA) !== VOTE_OPTIONS.NA)
        .map((task) => ({
          processId: task.processId,
          selectedRoutesId: selectedRoutes[task.processId],
          reference: task.reference,
          vote: voteValues[task.processId],
          fieldLevelComment: fieldComments[task.processId],
          overallComment: overallComments[task.processId],
        }))

      if (!selectedTasks.length) return

      setIsSaving(true)
      try {
        const postResults = await Promise.all(
          selectedTasks.map(
            ({ processId, selectedRoutesId, vote, fieldLevelComment, overallComment }) =>
              processDataService.createNextProcess(user, processId, selectedRoutesId, {
                action: vote,
                overall_comment: overallComment,
                field_level_comment: fieldLevelComment,
              }),
          ),
        )

        const successfulTasks = selectedTasks.filter((_, i) =>
          [200, 201].includes(postResults[i].status),
        )
        const unsuccessfulTasks = selectedTasks.filter(
          (_, i) => ![200, 201].includes(postResults[i].status),
        )

        if (successfulTasks.length) {
          toast.success(
            `${successfulTasks.map((t) => t.reference).join(', ')} saved successfully`,
            TOAST_SETTINGS,
          )

          await Promise.all(
            successfulTasks.map(({ processId, overallComment }) =>
              processDataService.saveOverallComments(
                user,
                processId,
                (overallComment || '').trim(),
              ),
            ),
          )
        }

        if (unsuccessfulTasks.length) {
          toast.error(
            `${unsuccessfulTasks.map((t) => t.reference).join(', ')} failed to save.`,
            TOAST_SETTINGS_ERROR,
          )
        }
      } catch {
        toast.error('Error saving data.', TOAST_SETTINGS_ERROR)
      } finally {
        setIsSaving(false)
      }
    },
    [groupedTasks, voteValues, selectedRoutes, overallComments, fieldComments, user],
  )

  const handlePrepareConfirmation = useCallback(
    (templateId) => {
      const group = groupedTasks[templateId]
      if (!group) return

      const selected = group.tasks.filter(
        (task) => (voteValues[task.processId] || VOTE_OPTIONS.NA) !== VOTE_OPTIONS.NA,
      )

      if (!selected.length) {
        toast.error('Please select Approve/Reject for at least one task.', TOAST_SETTINGS_ERROR)
        return
      }

      setConfirmationTitle(
        `Are you sure you want to submit: ${selected.map((t) => t.reference).join(', ')}?`,
      )
      setPendingTemplateId(templateId)
      setIsConfirmationBoxOpen(true)
    },
    [groupedTasks, voteValues],
  )

  const handlePreviewClick = useCallback((fileKey) => {
    setSelectedFileKey(fileKey)
    setIsFileViewerOpen(true)
  }, [])

  const axiosOptionsWithBlob = useCallback(
    () =>
      user ? { headers: { Authorization: `Bearer ${user.token}` }, responseType: 'blob' } : {},
    [user],
  )

  const getFileWithS3Key = useCallback(
    async (s3Key, index) => {
      setFileLoading((prev) => new Set([...prev, index]))
      try {
        const result = await axios.get(
          `${NODE_BASE_URL}/data-entry/file/${s3Key}`,
          axiosOptionsWithBlob(),
        )
        const url = window.URL.createObjectURL(new Blob([result.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', s3Key)
        document.body.appendChild(link)
        link.click()
        link.remove()
      } finally {
        setFileLoading((prev) => {
          const updated = new Set(prev)
          updated.delete(index)
          return updated
        })
      }
    },
    [axiosOptionsWithBlob],
  )

  const buildFileOptions = useCallback(
    (s3Key, idx) => [
      {
        label: 'Preview',
        icon: (
          <IconButton size="small">
            <Visibility />
          </IconButton>
        ),
        onClick: () => handlePreviewClick(s3Key),
      },
      {
        label: 'Download',
        icon: (
          <IconButton size="small">
            {fileLoading.has(idx) ? (
              <CircularProgress color="info" size="25px" />
            ) : (
              <GetApp color="info" size="small" />
            )}
          </IconButton>
        ),
        onClick: () => getFileWithS3Key(s3Key, idx),
      },
    ],
    [fileLoading, handlePreviewClick, getFileWithS3Key],
  )

  // Transform grouped tasks
  const updatedGroupedTasks = useMemo(() => {
    const allTasks = Object.values(groupedTasks).flatMap((g) => g.tasks)

    const extraColumns = (() => {
      const extra = [{ key: 'reference', label: 'Reference Number' }]
      if (activityType === ACTIVITY_TYPES.REVIEW) {
        if (allTasks.some((t) => t.status !== PROCESS_ACTIVITY_STATUSES.CLOSED))
          extra.push({ key: 'review', label: 'Review' })
        extra.push({ key: 'vote', label: 'Vote' }, { key: 'activity', label: 'Activity (v)' })
        if (allTasks.some((t) => t.status !== PROCESS_ACTIVITY_STATUSES.CLOSED))
          extra.push({ key: 'fieldLevelComment', label: 'Field-level Common Comment' })
        extra.push({ key: 'overallComment', label: 'Overall Comment' })
      }
      return extra
    })()

    return Object.fromEntries(
      Object.entries(groupedTasks).map(([templateId, group]) => {
        const fieldsWithIds = (group.fields || []).map((field, idx) => ({
          ...field,
          id: field.id ?? `original-${idx}`,
          nonHideable: field.key === 'process_reference_num',
        }))

        return [
          templateId,
          {
            ...group,
            extraColumns,
            fields: fieldsWithIds,
            finalFields: fieldsWithIds,
          },
        ]
      }),
    )
  }, [groupedTasks, activityType])

  // Column-Hiding
  const handleSetHiddenColumns = useCallback(
    (templateId, newHidden) => {
      const allFields = updatedGroupedTasks?.[templateId]?.finalFields || []
      const allowedHidden = newHidden.filter((id) => {
        const field = allFields.find((f) => f.id === id)
        return !field?.nonHideable
      })

      setHiddenColumnsByTemplate((prev) => ({
        ...prev,
        [activityType]: {
          ...(prev?.[activityType] || {}),
          [templateId]: allowedHidden,
        },
      }))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activityType, updatedGroupedTasks],
  )

  const usersMap = useMemo(
    () => Object.fromEntries((users || []).map((u) => [u.user_id, u])),
    [users],
  )

  const grayHeaderColor = '#f0f3fd'
  const tableCellStyle = { backgroundColor: grayHeaderColor, textAlign: 'center' }

  return (
    <div className="tableview">
      {noTasksForCurrentType ? (
        <Typography variant="body1" color="textSecondary" align="center">
          No tasks available for {activityType}
        </Typography>
      ) : (
        Object.entries(updatedGroupedTasks).map(([templateId, group], index) => {
          const hiddenColumns = hiddenColumnsByTemplate?.[activityType]?.[templateId] || []
          const { name, tasks, finalFields } = group
          const filteredVisibleFields = finalFields?.filter((f) => !hiddenColumns.includes(f.id))
          const isExpanding = expandingAccordionId === templateId
          const showSkeleton = skeletonAccordionId === templateId && expanded === index

          return (
            <div
              key={templateId}
              className="tableview-inside"
              style={{ padding: '0px 0px 10px', borderRadius: '14px', position: 'relative' }}
            >
              {isExpanding && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.5)',
                    zIndex: 100,
                    borderRadius: '14px',
                    backdropFilter: 'blur(1px)',
                  }}
                >
                  <CircularProgress size={40} />
                </Box>
              )}

              <Accordion
                expanded={index === expanded}
                className="accordian-shadow"
                onChange={(_, isExpanded) => {
                  if (isExpanded) {
                    setExpanded(index)
                    setSkeletonAccordionId(templateId)
                    setTimeout(() => {
                      setSkeletonAccordionId(null)
                    }, 300)
                  } else {
                    setExpanded(false)
                  }
                }}
              >
                <AccordionSummary
                  className="acoordionsummary"
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1-content"
                  id="panel1-header"
                >
                  <div
                    style={{
                      display: 'flex',
                      width: '100%',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <Typography className="textPrimary fontsize14 mb-0" variant="h6" gutterBottom>
                        {name}
                      </Typography>

                      {expanded === index && (
                        <div
                          onClick={(e) => e.stopPropagation()}
                          onFocus={(e) => e.stopPropagation()}
                          style={{ display: 'flex', justifyContent: 'flex-end' }}
                        >
                          <TableSetting
                            columns={getFieldForSetting(
                              finalFields?.filter((field) => !field.nonHideable),
                            )}
                            hiddenColumns={hiddenColumns}
                            setHiddenColumns={(newHidden) =>
                              handleSetHiddenColumns(templateId, newHidden)
                            }
                            showFreezing={false}
                            color="primary"
                          />
                        </div>
                      )}
                    </div>

                    <p className="textPrimary fontsize11 mb-0">
                      <span
                        style={{
                          padding: '3px 6px',
                          borderRadius: '10px',
                          backgroundColor: '#aeadfa',
                        }}
                      >
                        {tasks?.length || 0}
                      </span>
                    </p>
                  </div>
                </AccordionSummary>

                <AccordionDetails sx={{ p: 0 }}>
                  {showSkeleton ? (
                    <Box sx={{ p: 2 }}>
                      {[...Array(Math.min(tasks?.length || 0, 10))].map((_, row) => (
                        <Box key={row} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                          {[...Array(filteredVisibleFields.length + 5)].map((_, col) => (
                            <Skeleton
                              key={col}
                              variant="rounded"
                              height={35}
                              sx={{ flex: 1, width: '100px' }}
                            />
                          ))}
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    expanded === index && (
                      <TableContainer
                        key={name}
                        component={Paper}
                        style={{ marginBottom: '3px' }}
                        sx={{ boxShadow: '1px 2px 6px 0px rgb(57 85 120 / 11%)' }}
                      >
                        <Table className="responsiveTable">
                          <TableHead>
                            <TableRow>
                              <TableCell
                                className="fixed-width-cell sticky-left referenceNum-column"
                                sx={tableCellStyle}
                              >
                                Reference Number
                              </TableCell>

                              {activityType === ACTIVITY_TYPES.REVIEW ? (
                                <>
                                  {tasks.some(
                                    (task) => task.status !== PROCESS_ACTIVITY_STATUSES.CLOSED,
                                  ) && (
                                    <TableCell
                                      className="fixed-width-cell sticky-left-2 review-column"
                                      sx={tableCellStyle}
                                    >
                                      Review
                                    </TableCell>
                                  )}
                                  <TableCell className="fixed-width-cell" sx={tableCellStyle}>
                                    Vote
                                  </TableCell>
                                  <TableCell className="fixed-width-cell" sx={tableCellStyle}>
                                    Activity (v)
                                  </TableCell>
                                  {tasks.some(
                                    (task) => task.status !== PROCESS_ACTIVITY_STATUSES.CLOSED,
                                  ) && (
                                    <TableCell className="fixed-width-cell" sx={tableCellStyle}>
                                      Field-level Common Comment
                                    </TableCell>
                                  )}
                                  <TableCell className="fixed-width-cell" sx={tableCellStyle}>
                                    Overall Comment
                                  </TableCell>
                                </>
                              ) : (
                                <TableCell className="fixed-width-cell" sx={tableCellStyle}>
                                  Activity (v)
                                </TableCell>
                              )}

                              {filteredVisibleFields?.map((field) => (
                                <TableCell
                                  key={field.id}
                                  className="fixed-width-cell"
                                  sx={{
                                    minWidth: `${field.field_width_in_rem_for_process_dashboard}rem`,
                                    backgroundColor: grayHeaderColor,
                                    textAlign: 'center',
                                  }}
                                >
                                  {field.field_template_name}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>

                          <TableBody>
                            {tasks?.map((task, j) => {
                              const voteValue = voteValues[task.processId] || VOTE_OPTIONS.NA
                              const isDisabled = voteValue === VOTE_OPTIONS.NA

                              const voteOptions = task.voteOptions.filter((route) => {
                                if (task.status === PROCESS_ACTIVITY_STATUSES.CLOSED) return true
                                if (voteValue === VOTE_OPTIONS.APPROVE)
                                  return (
                                    route.route_type === ROUTE_TYPES.PROGRESS ||
                                    route.route_type === ROUTE_TYPES.NEUTRAL
                                  )
                                if (voteValue === VOTE_OPTIONS.REJECT)
                                  return (
                                    route.route_type === ROUTE_TYPES.REGRESS ||
                                    route.route_type === ROUTE_TYPES.NEUTRAL
                                  )
                                return false
                              })

                              return (
                                <TableRow
                                  key={`${templateId}-${task.processId}-${task.activityTemplateId}-${j}`}
                                  id={`${templateId}-${task.processId}-${task.activityTemplateId}`}
                                  style={j % 2 !== 0 ? { backgroundColor: '#fff' } : undefined}
                                >
                                  <Tooltip
                                    sx={{ padding: '8px', position: 'relative', top: '5px' }}
                                    title={
                                      task.status === PROCESS_ACTIVITY_STATUSES.ACTIVE
                                        ? !task.is_immediate_action_required &&
                                          task.activity_type === ACTIVITY_TYPES.LONG_RUNNING
                                          ? 'No immediate action required'
                                          : 'Work on task'
                                        : null
                                    }
                                  >
                                    <TableCell
                                      sx={{ padding: '8px' }}
                                      align="center"
                                      className="sticky-left"
                                      style={{
                                        color:
                                          task.status === PROCESS_ACTIVITY_STATUSES.ACTIVE
                                            ? '#6C47E4'
                                            : undefined,
                                        fontWeight: 700,
                                      }}
                                    >
                                      <span
                                        onClick={() => {
                                          const navCallback = () =>
                                            navigate(
                                              `/process-data/${task.processId}/${task.activityTemplateId}`,
                                            )
                                          saveScrollPosition()
                                          if (typeof onNavigate === 'function') {
                                            onNavigate(navCallback)
                                          } else {
                                            navCallback()
                                          }
                                        }}
                                        style={{
                                          cursor: 'pointer',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                        }}
                                      >
                                        {task.activity_type === ACTIVITY_TYPES.LONG_RUNNING &&
                                          !task.is_immediate_action_required && (
                                            <SnoozeIcon fontSize="small" />
                                          )}
                                        &nbsp;{task.reference}
                                      </span>
                                    </TableCell>
                                  </Tooltip>

                                  {activityType === ACTIVITY_TYPES.REVIEW ? (
                                    <>
                                      {task.status !== PROCESS_ACTIVITY_STATUSES.CLOSED && (
                                        <TableCell
                                          sx={{ padding: '0px' }}
                                          align="center"
                                          className="sticky-left-2"
                                        >
                                          <ApproveRejectSwitch
                                            index={`${templateId}-${task.processId}`}
                                            switchState={voteValue}
                                            handleSwitchChange={(processId, value) =>
                                              handleSwitchChange(processId, value, task.voteOptions)
                                            }
                                            taskReference={task.processId}
                                          />
                                        </TableCell>
                                      )}

                                      <TableCell
                                        sx={{ padding: '0px', minWidth: '180px' }}
                                        align="center"
                                      >
                                        {task.status === PROCESS_ACTIVITY_STATUSES.CLOSED ? (
                                          voteOptions.find(
                                            (it) => it.id === task.selectedRouteTemplateId,
                                          )?.name
                                        ) : (
                                          <Autocomplete
                                            size="small"
                                            key={voteValue}
                                            options={voteOptions}
                                            sx={{ width: '100%' }}
                                            disabled={isDisabled}
                                            value={
                                              voteOptions.find(
                                                (it) => it.id === selectedRoutes[task.processId],
                                              ) || voteOptions[0]
                                            }
                                            onChange={(_, newValue) =>
                                              handleVoteChange(task.processId, newValue?.id)
                                            }
                                            getOptionLabel={(option) => option?.name || ''}
                                            renderInput={(params) => (
                                              <TextField
                                                {...params}
                                                variant="outlined"
                                                size="small"
                                                InputProps={{
                                                  ...params.InputProps,
                                                  disableUnderline: true,
                                                  style: { border: 'none', outline: 'none' },
                                                }}
                                                sx={{
                                                  width: '100%',
                                                  '& .MuiOutlinedInput-root fieldset': {
                                                    border: 'none',
                                                  },
                                                  '& .MuiOutlinedInput-root:hover fieldset': {
                                                    border: 'none',
                                                  },
                                                  '& .MuiOutlinedInput-root.Mui-focused fieldset': {
                                                    border: 'none',
                                                  },
                                                }}
                                              />
                                            )}
                                          />
                                        )}
                                      </TableCell>

                                      <TableCell>
                                        <Typography
                                          sx={{
                                            fontSize: '12px',
                                            color: '#4b4f60',
                                            fontWeight: '600',
                                            width: '194px',
                                          }}
                                        >
                                          {task.activity_template_name} (v{task.process_version})
                                        </Typography>
                                      </TableCell>

                                      {task.status !== PROCESS_ACTIVITY_STATUSES.CLOSED && (
                                        <TableCell
                                          sx={{ padding: '0px', minWidth: '130px' }}
                                          align="left"
                                        >
                                          <TextField
                                            variant="outlined"
                                            size="small"
                                            disabled={isDisabled}
                                            multiline
                                            value={fieldComments[task.processId] || ''}
                                            onChange={(e) =>
                                              setFieldComments((prev) => ({
                                                ...prev,
                                                [task.processId]: e.target.value,
                                              }))
                                            }
                                            InputProps={{
                                              disableUnderline: true,
                                              style: { border: 'none', outline: 'none' },
                                            }}
                                            sx={{
                                              width: '100%',
                                              '& .MuiOutlinedInput-root fieldset': {
                                                border: 'none',
                                              },
                                              '& .MuiOutlinedInput-root:hover fieldset': {
                                                border: 'none',
                                              },
                                              '& .MuiOutlinedInput-root.Mui-focused fieldset': {
                                                border: 'none',
                                              },
                                            }}
                                          />
                                        </TableCell>
                                      )}

                                      <TableCell
                                        sx={{ padding: '0px', minWidth: '130px' }}
                                        align="left"
                                      >
                                        {task.status === PROCESS_ACTIVITY_STATUSES.CLOSED ? (
                                          <MultilineEllipsisWithTooltip
                                            text={task.overallComment || ''}
                                            maxLines={3}
                                          />
                                        ) : (
                                          <TextField
                                            size="small"
                                            multiline
                                            disabled={isDisabled}
                                            value={overallComments[task.processId] || ''}
                                            onChange={(e) =>
                                              setOverallComments((prev) => ({
                                                ...prev,
                                                [task.processId]: e.target.value,
                                              }))
                                            }
                                            variant="outlined"
                                            InputProps={{
                                              disableUnderline: true,
                                              style: { border: 'none', outline: 'none' },
                                            }}
                                            sx={{
                                              width: '100%',
                                              '& .MuiOutlinedInput-root fieldset': {
                                                border: 'none',
                                              },
                                              '& .MuiOutlinedInput-root:hover fieldset': {
                                                border: 'none',
                                              },
                                              '& .MuiOutlinedInput-root.Mui-focused fieldset': {
                                                border: 'none',
                                              },
                                            }}
                                          />
                                        )}
                                      </TableCell>
                                    </>
                                  ) : (
                                    <TableCell>
                                      <Typography
                                        sx={{
                                          fontSize: '12px',
                                          color: '#4b4f60',
                                          fontWeight: '600',
                                          width: '194px',
                                        }}
                                      >
                                        {task.activity_template_name} (v{task.process_version})
                                      </Typography>
                                    </TableCell>
                                  )}

                                  {filteredVisibleFields?.map((field, idx) => {
                                    const value = task.values[idx]
                                    let displayValue

                                    const cellAlign = [
                                      FIELD_TYPES.DATE,
                                      FIELD_TYPES.DATETIME,
                                      FIELD_TYPES.CURRENCY,
                                      FIELD_TYPES.NUMBER,
                                    ].includes(field.data_structure)
                                      ? 'right'
                                      : 'left'

                                    if (field.data_structure === FIELD_TYPES.DATE) {
                                      displayValue =
                                        value && moment(value).isValid()
                                          ? moment(value).format(FORMATS.DATE)
                                          : ''
                                    } else if (field.data_structure === FIELD_TYPES.DATETIME) {
                                      displayValue =
                                        value && moment(value).isValid()
                                          ? moment(value).format(FORMATS.DATETIME)
                                          : ''
                                    } else if (
                                      [
                                        FIELD_TYPES.TIMESTAMPED_BIGTEXT,
                                        FIELD_TYPES.TIMESTAMPED_DATE,
                                        FIELD_TYPES.TIMESTAMPED_DATETIME,
                                      ].includes(field.data_structure) &&
                                      Array.isArray(value)
                                    ) {
                                      const formattedJSX = value.map((item, i) => {
                                        const userObj = usersMap[item.user_id]
                                        const username = userObj ? formatUserFullName(userObj) : ''
                                        return (
                                          <React.Fragment key={i}>
                                            <strong style={{ color: '#4D5E80' }}>
                                              {item.timestamp}:
                                            </strong>{' '}
                                            {item.text}
                                            {username && (
                                              <span
                                                style={{
                                                  fontSize: '11px',
                                                  fontStyle: 'italic',
                                                  color: '#888',
                                                  marginLeft: 6,
                                                }}
                                              >
                                                {' - '}
                                                {username}
                                              </span>
                                            )}
                                            <br />
                                          </React.Fragment>
                                        )
                                      })
                                      displayValue = (
                                        <MultilineEllipsisWithTooltip
                                          text={formattedJSX}
                                          maxLines={3}
                                        />
                                      )
                                    } else if (field.data_structure === FIELD_TYPES.CURRENCY) {
                                      const ndcMap = (task.ndc || []).reduce(
                                        (acc, cur) => ({
                                          ...acc,
                                          [cur.field_template_id]: cur.currency_code,
                                        }),
                                        {},
                                      )
                                      displayValue = formatCurrency(value, ndcMap[field.id], {
                                        defaultCurrency: user?.default_currency_code,
                                      })
                                    } else if (field.data_structure === FIELD_TYPES.FILE) {
                                      displayValue = (
                                        <div
                                          style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                          }}
                                        >
                                          {value}
                                          {value && (
                                            <LongMenu options={buildFileOptions(value, idx)} />
                                          )}
                                        </div>
                                      )
                                    } else if (Array.isArray(value)) {
                                      const arrayDisplay = value
                                        .map((item) =>
                                          typeof item === 'object' && item !== null
                                            ? item.value || JSON.stringify(item)
                                            : item,
                                        )
                                        .filter(Boolean)
                                        .join(', ')
                                      displayValue = (
                                        <MultilineEllipsisWithTooltip
                                          text={arrayDisplay}
                                          maxLines={3}
                                        />
                                      )
                                    } else if (typeof value === 'string' && value.includes('\n')) {
                                      const namesOnly = value.replace(/\s*\([^)]*\)/g, '')
                                      displayValue = (
                                        <MultilineEllipsisWithTooltip
                                          text={namesOnly}
                                          maxLines={3}
                                        />
                                      )
                                    } else if (typeof value === 'object' && value !== null) {
                                      displayValue = (
                                        <MultilineEllipsisWithTooltip
                                          text={JSON.stringify(value)}
                                          maxLines={3}
                                        />
                                      )
                                    } else {
                                      displayValue = value
                                    }

                                    return (
                                      <TableCell
                                        key={field.id ?? idx}
                                        align={cellAlign}
                                        sx={{ padding: '8px' }}
                                      >
                                        <MultilineEllipsisWithTooltip
                                          text={displayValue}
                                          maxLines={3}
                                        />
                                      </TableCell>
                                    )
                                  })}
                                </TableRow>
                              )
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )
                  )}
                </AccordionDetails>
              </Accordion>

              {activityType === ACTIVITY_TYPES.REVIEW &&
                tasks?.length > 0 &&
                index === expanded &&
                tasks.some((task) => task.status === PROCESS_ACTIVITY_STATUSES.ACTIVE) && (
                  <Button
                    className="defalut-btn mt-1"
                    color="primary"
                    variant="contained"
                    onClick={() => handlePrepareConfirmation(templateId)}
                    disabled={isSaving}
                  >
                    {isSaving ? <CircularProgress size={24} /> : 'Go'}
                  </Button>
                )}
            </div>
          )
        })
      )}

      <ConfirmationBox
        isDialogOpen={isConfirmationBoxOpen}
        title={confirmationTitle}
        setIsDialogOpen={setIsConfirmationBoxOpen}
        onYes={() => {
          setIsConfirmationBoxOpen(false)
          if (pendingTemplateId) {
            handleGoClick(pendingTemplateId)
            setPendingTemplateId(null)
          }
        }}
      />

      <Dialog open={isSaving}>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Typography variant="h6" color="textSecondary" style={{ marginBottom: '10px' }}>
            Saving your response...
          </Typography>
          <CircularProgress color="primary" size={30} />
        </div>
      </Dialog>

      <FileViewer
        open={isFileViewerOpen}
        onClose={() => setIsFileViewerOpen(false)}
        fileKey={selectedFileKey}
        user={user}
        onDownload={
          selectedFileKey ? () => getFileWithS3Key(selectedFileKey, selectedFileKey) : undefined
        }
      />
    </div>
  )
}

export default MyTaskTableView
